import Alert from "@mui/material/Alert"
import Button from "@mui/material/Button"
import Card from "@mui/material/Card"
import CardContent from "@mui/material/CardContent"
import Chip from "@mui/material/Chip"
import MenuItem from "@mui/material/MenuItem"
import Stack from "@mui/material/Stack"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { useState } from "react"

import { previewMobileAppVersion } from "@domain/mobile_app_version"
import {
  type MobileAppVersionCheck,
  type MobilePlatform
} from "@models/mobile_app_version"

/**
 * "What will a device on version X see?"
 *
 * Calls the real public query rather than re-implementing the verdict here, so it
 * is a true end-to-end check of the code path a phone takes. Prefilled with the
 * versions currently in production so staff can confirm in one click that today's
 * build is not about to be blocked.
 */
const CHIP_COLOR: Record<
  MobileAppVersionCheck["updateType"],
  "default" | "info" | "error"
> = {
  NONE: "default",
  OPTIONAL: "info",
  FORCED: "error"
}

const DEFAULT_CURRENT: Record<MobilePlatform, string> = {
  IOS: "2.4.6",
  ANDROID: "2.10.9"
}

export default function VersionPreview({
  translate
}: {
  translate: (key: string) => string
}) {
  const [platform, setPlatform] = useState<MobilePlatform>("IOS")
  const [currentVersion, setCurrentVersion] = useState(DEFAULT_CURRENT.IOS)
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<MobileAppVersionCheck | null>(null)
  const [failed, setFailed] = useState(false)

  const run = async () => {
    setLoading(true)
    setFailed(false)
    const response = await previewMobileAppVersion({ platform, currentVersion })
    setLoading(false)
    if (!response.isSuccess || !response.data) {
      setResult(null)
      setFailed(true)
      return
    }
    setResult(response.data)
  }

  const onPlatformChange = (next: MobilePlatform) => {
    setPlatform(next)
    setCurrentVersion(DEFAULT_CURRENT[next])
    setResult(null)
  }

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" sx={{ mb: 1.5 }}>
          {translate("mobile_app_version_preview_title")}
        </Typography>

        <Stack direction="row" gap={1.5} flexWrap="wrap" alignItems="center">
          <TextField
            select
            size="small"
            label={translate("mobile_app_version_platform")}
            value={platform}
            onChange={(event) =>
              onPlatformChange(event.target.value as MobilePlatform)
            }
            sx={{ minWidth: "8rem" }}
          >
            <MenuItem value="IOS">iOS</MenuItem>
            <MenuItem value="ANDROID">Android</MenuItem>
          </TextField>

          <TextField
            size="small"
            label={translate("mobile_app_version_preview_current")}
            value={currentVersion}
            onChange={(event) => setCurrentVersion(event.target.value)}
            sx={{ minWidth: "10rem" }}
          />

          <Button variant="contained" onClick={run} disabled={loading}>
            {translate("mobile_app_version_preview_run")}
          </Button>
        </Stack>

        {failed && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {translate("mobile_app_version_preview_failed")}
          </Alert>
        )}

        {!!result && (
          <Stack gap={1} sx={{ mt: 2 }}>
            <Stack direction="row" gap={1} alignItems="center">
              <Typography variant="body2">
                {translate("mobile_app_version_update_type")}:
              </Typography>
              <Chip
                size="small"
                color={CHIP_COLOR[result.updateType]}
                label={translate(
                  `mobile_app_version_type_${result.updateType.toLowerCase()}`
                )}
              />
            </Stack>
            {result.updateType === "FORCED" && (
              <Alert severity="error">
                {translate("mobile_app_version_preview_forced_warning")}
              </Alert>
            )}
            {!!result.releaseNotes && (
              <Typography variant="caption" color="text.secondary">
                {result.releaseNotes.tr?.body ||
                  result.releaseNotes.en?.body ||
                  result.releaseNotes.ar?.body ||
                  "—"}
              </Typography>
            )}
          </Stack>
        )}
      </CardContent>
    </Card>
  )
}
